import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  Calendar,
  Clock,
  Activity,
  FileText,
  Share2,
  Download,
  Trash2,
  Users,
  Timer,
  FileAudio,
} from "lucide-react";

interface Recording {
  id: string;
  title: string;
  date: string;
  duration: string;
  status: "completed" | "processing" | "failed";
  summary?: string;
}

const DashboardContent = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("week");

  const analytics = {
    totalRecordings: 47,
    totalDuration: "23h 45m",
    avgAccuracy: "98.5%",
    activeUsers: 12,
  };

  const mockRecordings: Recording[] = [
    {
      id: "1",
      title: "Patient Consultation - John Doe",
      date: "2024-01-10",
      duration: "15:30",
      status: "completed",
      summary: "Discussion about treatment plan and medication adjustments.",
    },
    {
      id: "2",
      title: "Medical Review - Sarah Smith",
      date: "2024-01-09",
      duration: "22:45",
      status: "processing",
    },
    {
      id: "3",
      title: "Follow-up Notes - Mike Johnson",
      date: "2024-01-08",
      duration: "08:15",
      status: "completed",
      summary: "Progress review and updated recovery timeline.",
    },
    // Add more mock recordings to test scrolling
    ...Array.from({ length: 5 }, (_, i) => ({
      id: `${i + 4}`,
      title: `Sample Recording ${i + 4}`,
      date: "2024-01-07",
      duration: "10:00",
      status: "completed" as const,
      summary: "Additional recording for testing scroll behavior.",
    })),
  ];

  const getStatusColor = (status: Recording["status"]) => {
    switch (status) {
      case "completed":
        return "bg-green-500";
      case "processing":
        return "bg-blue-500";
      case "failed":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <header className="flex-none bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between max-w-7xl mx-auto gap-4 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Welcome back, Dr. Smith
            </h1>
            <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
              Here's what's happening with your recordings
            </p>
          </div>
          <Link
            to="/visit"
            className="inline-flex items-center justify-center px-4 sm:px-5 py-2 sm:py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30 w-full sm:w-auto"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Visit
          </Link>
        </div>
      </header>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
          {/* Analytics Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              {
                icon: (
                  <FileAudio className="w-6 sm:w-7 h-6 sm:h-7 text-blue-600" />
                ),
                label: "Total",
                value: analytics.totalRecordings,
                subtitle: "Recordings",
                bgColor: "bg-blue-50",
              },
              {
                icon: (
                  <Timer className="w-6 sm:w-7 h-6 sm:h-7 text-green-600" />
                ),
                label: "Duration",
                value: analytics.totalDuration,
                subtitle: "Total Time",
                bgColor: "bg-green-50",
              },
              {
                icon: (
                  <Activity className="w-6 sm:w-7 h-6 sm:h-7 text-purple-600" />
                ),
                label: "Accuracy",
                value: analytics.avgAccuracy,
                subtitle: "Average",
                bgColor: "bg-purple-50",
              },
              {
                icon: (
                  <Users className="w-6 sm:w-7 h-6 sm:h-7 text-orange-600" />
                ),
                label: "Active",
                value: analytics.activeUsers,
                subtitle: "Team Members",
                bgColor: "bg-orange-50",
              },
            ].map((card, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className={`p-2 sm:p-2.5 ${card.bgColor} rounded-xl`}>
                    {card.icon}
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-gray-500">
                    {card.label}
                  </span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-3 sm:mt-4">
                  {card.value}
                </h3>
                <p className="text-sm sm:text-base font-medium text-gray-500 mt-1">
                  {card.subtitle}
                </p>
              </div>
            ))}
          </div>

          {/* Recent Recordings */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  Recent Recordings
                </h2>
                <div className="flex items-center space-x-2 overflow-x-auto sm:overflow-visible">
                  {["week", "month", "year"].map((period) => (
                    <button
                      key={period}
                      onClick={() => setSelectedPeriod(period)}
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap ${
                        selectedPeriod === period
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search */}
              <div className="relative mt-4 sm:mt-6">
                <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search notes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 sm:py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm sm:text-base focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all outline-none"
                />
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {mockRecordings.map((recording) => (
                <div
                  key={recording.id}
                  className="p-4 sm:p-6 hover:bg-gray-50 transition-colors cursor-pointer group"
                >
                  <div className="flex items-start sm:items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 sm:mb-3 gap-2 sm:gap-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate pr-4">
                          {recording.title}
                        </h3>
                        <div className="flex items-center space-x-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                          <button className="p-2 sm:p-2.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                            <Share2 className="w-4 sm:w-5 h-4 sm:h-5" />
                          </button>
                          <button className="p-2 sm:p-2.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                            <Download className="w-4 sm:w-5 h-4 sm:h-5" />
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                        <div className="flex items-center text-xs sm:text-sm font-medium text-gray-500">
                          <Calendar className="w-4 h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                          {recording.date}
                        </div>
                        <div className="flex items-center text-xs sm:text-sm font-medium text-gray-500">
                          <Clock className="w-4 h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                          {recording.duration}
                        </div>
                        <div className="flex items-center">
                          <div
                            className={`w-2 h-2 rounded-full mr-1.5 sm:mr-2 ${getStatusColor(
                              recording.status
                            )}`}
                          />
                          <span className="text-xs sm:text-sm font-medium text-gray-500 capitalize">
                            {recording.status}
                          </span>
                        </div>
                      </div>
                      {recording.summary && (
                        <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-gray-600 leading-relaxed">
                          {recording.summary}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardContent;
